import AppError from '@/components/AppError';
import { TappFormField } from '@/components/ui/TappFormField';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappSelect } from '@/components/ui/TappSelect';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { cn } from '@/components/ui/utils';
import { Driver, UserStatus } from '@/lib/models/user';
import { Company } from '@/lib/models/company';
import { ApiUtils } from '@/lib/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAlert, usePrompt } from '@/components/ui/alert-dialog-provider';

const DriverDetailsPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('editDriver')} defaultBack backLink="/admin/drivers" />
      <Details />
    </>
  );
};

const userStatusOptions = [
  { label: 'Activ', value: UserStatus.active },
  { label: 'Suspendat', value: UserStatus.suspended },
  { label: 'Blocat', value: UserStatus.blocked },
  { label: 'În așteptare', value: UserStatus.pending },
];

const driverSchema = z.object({
  firstName: z.string().min(1, 'Prenumele este obligatoriu'),
  lastName: z.string().min(1, 'Numele este obligatoriu'),
  phoneNumber: z
    .string({ required_error: 'Numărul de telefon este obligatoriu' })
    .min(1, 'Numărul de telefon este obligatoriu'),
  carModel: z.string().min(1, 'Modelul mașinii este obligatoriu'),
  licensePlate: z.string().min(1, 'Numărul de înmatriculare este obligatoriu'),
  badgeNumber: z.string().min(2, 'Indicativul este obligatoriu').max(50, 'Indicativul este prea lung'),
  status: z.nativeEnum(UserStatus, { required_error: 'Statusul este obligatoriu' }),
  companyId: z.string().min(1, 'Compania este obligatorie'),
  companyName: z.string(),
  suspendedUntil: z.date().optional(),
});

type DriverFormData = z.infer<typeof driverSchema>;

const Details = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();
  const prompt = usePrompt();
  const originalStatusRef = useRef<UserStatus | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [`driver/${id}`],
    queryFn: () => ApiUtils.get<Driver>(`users/drivers/${id}`),
  });

  const { data: companies = [], isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await ApiUtils.get<Company[]>('company');
      return Array.isArray(response) ? response : [];
    },
  });

  const companyOptions = companies.map((company) => ({
    label: company.name,
    value: company._id,
  }));

  const { mutateAsync: updateDriver } = useMutation({
    mutationFn: (data: DriverFormData) => ApiUtils.put(`users/drivers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      queryClient.invalidateQueries({ queryKey: [`driver/${id}`] });
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isSubmitting, isSubmitted, errors },
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      carModel: '',
      licensePlate: '',
      badgeNumber: '',
      status: UserStatus.pending,
      companyId: '',
      companyName: '',
      suspendedUntil: undefined,
    },
  });

  useEffect(() => {
    if (data) {
      // Handle both old format (company) and new format (companyId)
      const companyId = (data as any).companyId || (data as any).company || data.companyId;

      console.log('Driver data received:', data);
      console.log('Setting companyId to:', companyId);

      // Store the original status
      originalStatusRef.current = data.status;

      reset({
        firstName: data.firstName || data.fullName?.split(' ')[0] || '',
        lastName: data.lastName || data.fullName?.split(' ').slice(1).join(' ') || '',
        phoneNumber: data.phoneNumber,
        carModel: data.carModel,
        licensePlate: data.licensePlate,
        badgeNumber: data.badgeNumber || '',
        status: data.status,
        companyId: companyId,
        companyName: data.companyName || '',
        suspendedUntil: data.suspendedUntil ? new Date(data.suspendedUntil) : undefined,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formData: DriverFormData) => {
    console.log('Submitting driver data:', formData);
    console.log('Company ID being sent:', formData.companyId);
    console.log('Company Name being sent:', formData.companyName);

    // Check if status changed to suspended
    if (formData.status === UserStatus.suspended && originalStatusRef.current !== UserStatus.suspended) {
      const hours = await prompt({
        title: t('suspendDriver'),
        body: t('suspendDriverPromptBody', { name: `${formData.firstName} ${formData.lastName}` }),
        cancelButton: t('cancel'),
        actionButton: t('confirmSuspend'),
        defaultValue: '24',
        inputProps: {
          type: 'number',
          min: '1',
          placeholder: t('hoursPlaceholder'),
        },
      });

      if (!hours) return;

      const hoursNum = parseInt(hours, 10);
      if (isNaN(hoursNum) || hoursNum < 1) {
        await alert({
          title: t('error'),
          body: t('invalidHoursError'),
        });
        return;
      }

      // Call the suspend endpoint with hours
      try {
        await ApiUtils.put(`users/drivers/${id}/suspend`, { hours: hoursNum });
        queryClient.invalidateQueries({ queryKey: ['drivers'] });
        queryClient.invalidateQueries({ queryKey: [`driver/${id}`] });
        await alert({
          title: t('success'),
          body: t('driverUpdated'),
        });
        navigate('/admin/drivers');
      } catch (err: any) {
        console.error(err);
        await alert({
          title: t('error'),
          body: ApiUtils.convertApiErrorToString(err),
        });
      }
      return;
    }

    try {
      // Prepare the data to send
      const dataToSend: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        carModel: formData.carModel,
        licensePlate: formData.licensePlate,
        badgeNumber: formData.badgeNumber,
        companyId: formData.companyId,
        companyName: formData.companyName,
      };

      // Only include status if it changed (to avoid backend validation when editing suspended drivers)
      if (formData.status !== originalStatusRef.current) {
        dataToSend.status = formData.status;
        // If changing to suspended, include suspendedUntil (though this should not happen here)
        if (formData.status === UserStatus.suspended && formData.suspendedUntil) {
          dataToSend.suspendedUntil = formData.suspendedUntil.toISOString();
        }
      }

      await updateDriver(dataToSend);
      await alert({
        title: t('success'),
        body: t('driverUpdated'),
      });
      navigate('/admin/drivers');
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  if (error) return <AppError error={error} />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex w-full max-w-md flex-1 flex-col items-start gap-2', { isLoading })}
    >
      <TappFormField>
        <TappLabel htmlFor="firstName">{t('firstName')}</TappLabel>
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('firstName')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="lastName">{t('lastName')}</TappLabel>
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('lastName')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="phoneNumber">{t('phone')}</TappLabel>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput className="w-full" placeholder="+40741237583" {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="carModel">{t('car')}</TappLabel>
        <Controller
          name="carModel"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('car')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="licensePlate">{t('licensePlate')}</TappLabel>
        <Controller
          name="licensePlate"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('licensePlate')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="badgeNumber">{t('badgeNumber')}</TappLabel>
        <Controller
          name="badgeNumber"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'D-12345'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="companyId">{t('company')}</TappLabel>
        <Controller
          name="companyId"
          control={control}
          render={({ field: { value, onChange, name, onBlur }, fieldState }) => (
            <TappSelect
              className="w-full"
              options={companyOptions}
              value={companyOptions.find((c) => c.value === value)}
              onChange={(val) => {
                onChange(val?.value);
                setValue('companyName', val?.label || '');
              }}
              name={name}
              onBlur={onBlur}
              isLoading={isLoadingCompanies}
              isSubmitted={isSubmitted}
              {...fieldState}
            />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="status">{t('status')}</TappLabel>
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange, name, onBlur }, fieldState }) => (
            <TappSelect
              className="w-full"
              options={userStatusOptions}
              value={userStatusOptions.find((c) => c.value === value)}
              onChange={(val) => onChange(val?.value)}
              name={name}
              onBlur={onBlur}
              {...fieldState}
            />
          )}
        />
      </TappFormField>

      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {t('save')}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate('/admin/drivers')}>
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};

export default DriverDetailsPage;
